import { useCallback, useState, useRef } from 'react';
import { templateMigrationService } from '@/services/TemplateMigrationService';

interface MigrationProgress {
  total: number;
  processed: number;
  successful: number;
  failed: number;
  skipped: number;
  errors: string[];
}

interface MigrationState {
  isRunning: boolean;
  hasCompleted: boolean;
  hasError: boolean;
  errorMessage?: string;
  progress?: MigrationProgress;
}

interface MigrationTriggerOptions {
  enabled?: boolean;
  autoStart?: boolean;
  onSuccess?: () => void;
  onError?: (error: string) => void;
  onProgress?: (progress: MigrationProgress) => void;
}

export const useTemplateMigrationTrigger = (options: MigrationTriggerOptions = {}) => {
  const {
    enabled = true,
    autoStart = true,
    onSuccess,
    onError,
    onProgress
  } = options;

  const [migrationState, setMigrationState] = useState<MigrationState>({
    isRunning: false,
    hasCompleted: false,
    hasError: false
  });

  const hasTriggeredRef = useRef(false);
  const migrationRunningRef = useRef(false);

  const runMigration = useCallback(async () => {
    if (!enabled || migrationRunningRef.current || hasTriggeredRef.current) {
      return;
    }

    // Check if user has already been migrated successfully
    const migrationKey = 'template_migration_completed';
    const migrationStatus = sessionStorage.getItem(migrationKey);
    
    // Check if user creation has failed recently (to prevent repeated attempts)
    const userCreationFailedKey = 'user_creation_failed';
    const userCreationFailed = sessionStorage.getItem(userCreationFailedKey);
    
    if (migrationStatus === 'true') {
      setMigrationState(prev => ({
        ...prev,
        hasCompleted: true
      }));
      return;
    }

    if (userCreationFailed === 'true') {
      setMigrationState(prev => ({
        ...prev,
        hasError: true,
        errorMessage: 'User creation failed. Please try manual migration or contact support.'
      }));
      return;
    }
    
    hasTriggeredRef.current = true;
    migrationRunningRef.current = true;
    
    setMigrationState(prev => ({
      ...prev,
      isRunning: true,
      hasError: false,
      errorMessage: undefined
    }));

    try {
      const result = await templateMigrationService.migrateAllTemplates({
        dryRun: false,
        batchSize: 3,
        delayBetweenBatches: 1500,
        maxRetries: 2
      });

      setMigrationState(prev => ({
        ...prev,
        isRunning: false,
        hasCompleted: true,
        progress: result
      }));


      const strictCondition = result.failed === 0 && result.errors.length === 0 && result.total > 0;
      const lenientCondition = result.total === 0 && result.failed === 0 && result.errors.length === 0;
      


      const shouldMarkCompleted = (
        (result.failed === 0 && result.errors.length === 0 && result.total > 0) ||
        (result.total === 0 && result.failed === 0 && result.errors.length === 0)
      );

      if (shouldMarkCompleted) {
        sessionStorage.setItem(migrationKey, 'true');
      }

      onProgress?.(result);
      onSuccess?.();

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown migration error';
      console.error('[MIGRATION-TRIGGER] Migration failed:', errorMessage);
      
      // Check if this is a user creation error
      const userCreationFailedKey = 'user_creation_failed';
      if (errorMessage.includes('profiles_id_fkey') || 
          errorMessage.includes('null value in column "id"') || 
          errorMessage.includes('User creation failed') ||
          errorMessage.includes('User validation failed')) {
        

        sessionStorage.setItem(userCreationFailedKey, 'true');
      }
      
      setMigrationState(prev => ({
        ...prev,
        isRunning: false,
        hasError: true,
        errorMessage
      }));

      onError?.(errorMessage);
    } finally {
      migrationRunningRef.current = false;
    }
  }, [enabled, onSuccess, onError, onProgress]);

  const resetMigration = useCallback(() => {
    hasTriggeredRef.current = false;
    migrationRunningRef.current = false;
    setMigrationState({
      isRunning: false,
      hasCompleted: false,
      hasError: false
    });
    
    // Also clear user creation failed flag when resetting
    const userCreationFailedKey = 'user_creation_failed';
    sessionStorage.removeItem(userCreationFailedKey);
  }, []);

  const triggerMigration = useCallback(() => {
    if (autoStart && enabled) {
      // Add a small delay to ensure user initialization is complete
      setTimeout(() => {
        runMigration();
      }, 2000);
    }
  }, [autoStart, enabled, runMigration]);

  // Manual trigger for testing or user-initiated migration
  const manualTrigger = useCallback(() => {
    resetMigration();
    runMigration();
  }, [resetMigration, runMigration]);

  // Clear migration flag to force re-migration (for testing)
  const clearMigrationFlag = useCallback(() => {
    const migrationKey = 'template_migration_completed';
    const userCreationFailedKey = 'user_creation_failed';
    sessionStorage.removeItem(migrationKey);
    sessionStorage.removeItem(userCreationFailedKey);

  }, []);

  // Clear user creation failed flag to allow retries
  const clearUserCreationFailedFlag = useCallback(() => {
    const userCreationFailedKey = 'user_creation_failed';
    sessionStorage.removeItem(userCreationFailedKey);

  }, []);

  return {
    migrationState,
    triggerMigration,
    manualTrigger,
    resetMigration,
    clearMigrationFlag,
    clearUserCreationFailedFlag,
    isRunning: migrationState.isRunning,
    hasCompleted: migrationState.hasCompleted,
    hasError: migrationState.hasError,
    errorMessage: migrationState.errorMessage,
    progress: migrationState.progress
  };
}; 